import { createContext, useContext, useLayoutEffect } from "react";

type LayoutVisibilityContextValue = {
  /** Whether the surrounding app shell (nav header + sidebar) is hidden. */
  hideLayout: boolean;
  /** Show or hide the surrounding app shell. */
  setHideLayout: (hide: boolean) => void;
};

const LayoutVisibilityContext = createContext<LayoutVisibilityContextValue>({
  hideLayout: false,
  setHideLayout: () => {},
});

export const LayoutVisibilityProvider = LayoutVisibilityContext.Provider;

/**
 * Access and imperatively toggle the app shell (nav header + sidebar) from
 * within a page or component. Callers that simply want the shell hidden for
 * their whole lifetime should prefer {@link useHideLayout}.
 */
export const useLayoutVisibility = () => useContext(LayoutVisibilityContext);

/**
 * Hide the app shell (nav header + sidebar) while the calling component is
 * mounted, restoring it on unmount. Pass a condition to toggle at runtime
 * (e.g. `useHideLayout(!showPrompt)`); it defaults to always hiding. Use this
 * for full-screen pages such as interstitial redirects. Runs as a layout
 * effect so the shell never flashes before it is hidden.
 */
export const useHideLayout = (hide: boolean = true) => {
  const { setHideLayout } = useLayoutVisibility();
  useLayoutEffect(() => {
    setHideLayout(hide);
    return () => setHideLayout(false);
  }, [setHideLayout, hide]);
};
